import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { usePageMeta } from '@/hooks/usePageMeta';
import { TableOfContents } from '@/components/content/TableOfContents';
import { ArticleFeedback } from '@/components/content/ArticleFeedback';

const ARTICLE_DESCRIPTION =
  "One $500 ask is hard to make and easy to say no to. Here's why recurring monthly support outraises one-time asks for youth athletes over a season — and exactly how to ask for it.";

const FAQS = [
  {
    q: 'Is it rude to ask supporters for monthly support instead of a one-time gift?',
    a: "No — most people find a small recurring amount easier to say yes to than a single large ask. $15 a month feels more approachable than $180 all at once, even though it adds up to the same total across a season.",
  },
  {
    q: 'How much should I ask supporters to give each month?',
    a: "$10–$25 a month is a comfortable range for most family and friends. It's small enough to feel low-risk to the supporter, and ten people at that range covers real season costs (travel, fees, gear) without needing one big donor.",
  },
  {
    q: 'Can a supporter cancel a recurring donation whenever they want?',
    a: "Yes. Recurring support on Sport Fund is a standing monthly donation the supporter controls — they can cancel or change it anytime from their own account, no need to contact the athlete or ask permission.",
  },
  {
    q: "What if someone wants to give once instead of committing monthly?",
    a: "Both options sit on the same fund page. A one-time gift is still valuable, especially around a specific moment like making a team or a tournament — recurring support is just the better default to lead with because it keeps working after the first ask.",
  },
  {
    q: 'Does Sport Fund take a cut of recurring donations?',
    a: 'No. You keep 100% of every donation, one-time or recurring. A small platform fee (roughly 5–7%) is shown up front and added on top for the supporter at checkout — never taken out of what you raise.',
  },
];

const RecurringVsOneTimeDonations: React.FC = () => {
  usePageMeta('Recurring Monthly Donations vs. One-Time Gifts for Athletes', ARTICLE_DESCRIPTION, {
    article: true,
    image: '/resources/athlete-celebration.jpg',
    breadcrumbs: [
      { name: 'Resources', path: '/resources' },
      { name: 'Recurring vs. One-Time Donations' },
    ],
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: FAQS.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    },
  });

  const TOC_SECTIONS = [
    { id: 'why-one-big-ask-is-hard-to-make-and-easy-to-refuse', text: 'Why one big ask is hard to make — and easy to refuse' },
    { id: 'what-recurring-support-actually-looks-like', text: 'What recurring support actually looks like' },
    { id: 'the-math-ten-supporters-at-15-a-month', text: 'The math: ten supporters at $15 a month' },
    { id: 'how-to-ask-for-a-monthly-commitment', text: 'How to ask for a monthly commitment' },
    { id: 'when-a-one-time-gift-still-makes-sense', text: 'When a one-time gift still makes sense' },
    { id: 'how-sport-fund-handles-this', text: 'How Sport Fund handles this' },
    { id: 'frequently-asked-questions', text: 'Frequently asked questions' },
    { id: 'read-next', text: 'Read next' },
  ];

  return (
    <div className="max-w-3xl mx-auto px-4 py-12">
      <Link to="/resources" className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 mb-6">
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Resources
      </Link>
      <h1 className="text-3xl md:text-4xl font-bold mb-4">
        Recurring monthly donations vs. one-time gifts: what actually covers a season
      </h1>
      <p className="text-gray-500 mb-8 text-lg">
        A single big ask is hard to make and easy to refuse. Here's why recurring monthly support tends to
        raise more over a season than a one-time appeal — and how to ask for it without it feeling awkward.
      </p>

      <article className="prose prose-lg max-w-none">
        <TableOfContents sections={TOC_SECTIONS} />

        <h2 id="why-one-big-ask-is-hard-to-make-and-easy-to-refuse">
          Why one big ask is hard to make — and easy to refuse
        </h2>
        <p>
          "Can you help cover my $600 season?" is a hard message to send and a hard one to answer. For the
          athlete, it means picking one uncomfortable number and asking for all of it at once. For the
          supporter, saying yes means committing real money on the spot — so a lot of people default to "maybe
          later," which usually means never.
        </p>
        <p>
          A recurring ask flips both sides of that. "Could you chip in $15 a month this season?" is a smaller,
          easier yes — and because it renews automatically, the athlete isn't stuck re-asking the same person
          every few weeks when a new cost comes up.
        </p>

        <h2 id="what-recurring-support-actually-looks-like">What recurring support actually looks like</h2>
        <p>
          Recurring support is a standing monthly donation a supporter sets up once on an athlete's fund page.
          It renews automatically until the supporter changes or cancels it themselves — there's no invoice to
          send, no reminder text to write, no awkward follow-up. The athlete's page balance simply grows a
          little every month from the same group of people who already said yes once.
        </p>

        <h2 id="the-math-ten-supporters-at-15-a-month">The math: ten supporters at $15 a month</h2>
        <p>
          Ten monthly supporters at $15 each add up to <strong>$150 a month</strong> — roughly <strong>$1,200
          across an eight-month season</strong>, which covers most families' full-season spend without a single
          $500+ ask. Compare that to a car-wash-style team fundraiser, which typically nets only
          $1,000–$3,000 <em>for an entire team</em>, split many ways. Ten people saying yes to something small
          usually beats a handful of people being asked for something big.
        </p>

        <h2 id="how-to-ask-for-a-monthly-commitment">How to ask for a monthly commitment</h2>
        <p>
          Lead with the recurring option, not the one-time one, and be specific about what it's for:
        </p>
        <blockquote>
          "This season's meets and travel run about $150 a month — if 8–10 people could chip in $15/month, that
          covers it. Here's my page: [link]. You can cancel anytime, no pressure either way."
        </blockquote>
        <p>
          Naming the monthly number (not just the season total) makes the ask concrete, and naming that it's
          cancel-anytime lowers the perceived risk of saying yes. See our{' '}
          <Link to="/resources/fundraising-letter-template" className="text-blue-600">
            fundraising letter template
          </Link>{' '}
          for a full version of this ask you can copy and personalize.
        </p>

        <h2 id="when-a-one-time-gift-still-makes-sense">When a one-time gift still makes sense</h2>
        <p>
          Recurring support is the better default, but it isn't the only option. A specific moment — making
          varsity, qualifying for a tournament, an unexpected travel cost — is a natural fit for a one-time
          gift, especially from someone who wouldn't commit to a monthly amount. Offer both on the same page
          rather than picking one; different supporters will reach for different ones.
        </p>

        <h2 id="how-sport-fund-handles-this">How Sport Fund handles this</h2>
        <p>
          Every Sport Fund page supports both one-time and recurring donations from the same link — supporters
          choose which one fits them at checkout. A few things we're deliberate about:
        </p>
        <ul>
          <li>
            <strong>The supporter controls the recurring gift</strong> — they can change the amount or cancel
            it anytime from their own account, without contacting the athlete.
          </li>
          <li>
            <strong>You keep 100% of every donation</strong>, one-time or recurring. The small platform fee
            (roughly 5–7%) is added on top for the supporter at checkout — never taken out of what you raise.
          </li>
          <li>
            <strong>Payouts go straight to your family's bank</strong>, not a club or team account.
          </li>
        </ul>
        <p>
          <Link to="/signup-creator" className="text-blue-600">
            Start a free fund page
          </Link>{' '}
          and add a recurring option to your very first ask.
        </p>

        <h2 id="frequently-asked-questions">Frequently asked questions</h2>
        <div className="not-prose space-y-6">
          {FAQS.map((f) => (
            <div key={f.q}>
              <h3 className="font-semibold text-lg text-gray-900 mb-1">{f.q}</h3>
              <p className="text-gray-600">{f.a}</p>
            </div>
          ))}
        </div>

        <h2 id="read-next">Read next</h2>
        <p>
          <Link to="/resources/how-to-fundraise-for-a-sports-season" className="text-blue-600">
            How to Fundraise for Your Sports Season: 7 Honest Ways That Actually Work
          </Link>{' '}
          — recurring support is #1 on the list; here's the rest.
        </p>
        <p>
          <Link to="/resources/how-to-set-a-fundraising-goal" className="text-blue-600">
            How to Set a Fundraising Goal for Your Sports Season
          </Link>{' '}
          — turn a season's real costs into the monthly number to ask for.
        </p>
        <p>
          <Link to="/resources/fundraising-letter-template" className="text-blue-600">
            Sports Fundraising Letter Template: A Free Letter You Can Copy
          </Link>{' '}
          — a full letter built around the recurring ask above.
        </p>
        <ArticleFeedback articleSlug="recurring-vs-one-time-donations" />
      </article>
    </div>
  );
};

export default RecurringVsOneTimeDonations;
